/**
 * ModelPicker — reusable per-task model selector (L-model).
 *
 * "Auto" (best-fit routing, the default) plus the admin allow-listed models,
 * each with its per-model CREDIT cost. Credit-priced only; NEVER plan-gated.
 * Controlled: `value` is the selected model_id or "auto"; `onChange` gets the id.
 *
 * Mount it in an AI composer next to the send button; pass the chosen id into
 * that request's `model` option (the copilot forwards it as `options.model`,
 * enforced against the same allow-list server-side).
 */
import { useEffect, useState } from 'react';

import { listPickerModels, type PickerModel, type PickerModelsResponse } from '@/lib/api/credits';

export const MODEL_AUTO = 'auto';

function fmtCr(n: number): string {
  return n.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function costLabel(m: PickerModel): string {
  return `${fmtCr(m.credits_per_1k_input)}/${fmtCr(m.credits_per_1k_output)} cr par 1k tokens (in/out)`;
}

interface ModelPickerProps {
  value?: string | null;
  onChange: (modelId: string) => void;
  className?: string;
  label?: string;
  disabled?: boolean;
  /** Compact (no caption) — for a tight composer toolbar. */
  compact?: boolean;
}

export function ModelPicker({
  value,
  onChange,
  className,
  label = 'Modèle',
  disabled,
  compact,
}: ModelPickerProps) {
  const [data, setData] = useState<PickerModelsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listPickerModels()
      .then((res) => {
        if (alive) setData(res);
      })
      .catch(() => {
        // Degrade to Auto-only: the picker still works (best-fit routing).
        if (alive) setData({ default: { id: MODEL_AUTO, label: 'Auto', auto: true }, models: [], count: 0 });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const current = value && value.trim() ? value : MODEL_AUTO;
  const selected = data?.models.find((m) => m.model_id === current) ?? null;

  return (
    <label className={`inline-flex flex-col gap-1 text-xs ${className ?? ''}`}>
      {label && <span className="font-medium text-muted">{label}</span>}
      <select
        value={current}
        disabled={disabled || loading}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-token border border-border bg-surface px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
        aria-label={label}
        title={selected ? costLabel(selected) : 'Modèle choisi automatiquement (meilleur choix)'}
      >
        <option value={MODEL_AUTO}>Auto (recommandé)</option>
        {(data?.models ?? []).map((m) => (
          <option key={m.id} value={m.model_id}>
            {m.provider} · {m.label} — {fmtCr(m.credits_per_1k_output)} cr/1k out
          </option>
        ))}
      </select>
      {!compact && (
        <span className="text-[11px] text-muted">
          {current === MODEL_AUTO
            ? 'Modèle choisi automatiquement selon la tâche.'
            : selected
              ? costLabel(selected)
              : 'Modèle spécifique.'}
        </span>
      )}
    </label>
  );
}
